import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { 
  AlertTriangle, 
  Heart, 
  Flame, 
  Users, 
  HelpCircle,
  MapPin,
  ArrowLeft,
  Send,
  Mic,
  MicOff,
  Loader2,
  UserCheck,
  UsersRound
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALERT_CATEGORIES = [
  { id: 'threat', label: 'Threat', icon: AlertTriangle, color: '#ef4444', description: 'Armed person, violence, active threat' },
  { id: 'disturbance', label: 'Disturbance', icon: Users, color: '#f97316', description: 'Disruptive behavior, argument, noise' },
  { id: 'medical', label: 'Medical', icon: Heart, color: '#0ea5e9', description: 'Medical emergency, injury, health crisis' },
  { id: 'child_safety', label: 'Child Safety', icon: UsersRound, color: '#a855f7', description: 'Missing child, child in danger, custody issue' },
  { id: 'fire', label: 'Fire', icon: Flame, color: '#eab308', description: 'Fire, smoke, evacuation needed' },
  { id: 'general', label: 'General', icon: HelpCircle, color: '#a1a1aa', description: 'Other security concern' },
];

const LOCATIONS = [
  'Nursery', 'Sunday School', 
  'Sanctuary Main', 'Sanctuary Up', 
  'Foyer', 'Office',
  'Large Hall', 'Small Hall', 
  'Baptistry / Music', 'Parking Lot'
];

const PanicPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [description, setDescription] = useState('');
  const [sending, setSending] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  // Team member selection
  const [team, setTeam] = useState([]);
  const [alertMode, setAlertMode] = useState('all'); // 'all' or 'selected'
  const [selectedMembers, setSelectedMembers] = useState([]);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  
  // Check if this is a non-emergency alert
  const isNonEmergency = searchParams.get('severity') === 'non_emergency';

  // Check for category in URL params and skip to step 2
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      const category = ALERT_CATEGORIES.find(c => c.id === categoryParam);
      if (category) {
        setSelectedCategory(category);
        setStep(2);
      }
    }
  }, [searchParams]);

  // Fetch team members
  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const response = await axios.get(`${API_URL}/team`);
        setTeam(response.data.filter(m => m.id !== user?.id)); // Exclude self
      } catch (error) {
        console.error('Failed to fetch team:', error);
      }
    };
    fetchTeam();
  }, [user]);

  const toggleMember = (memberId) => {
    setSelectedMembers(prev => 
      prev.includes(memberId) 
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const selectAllMembers = () => {
    setSelectedMembers(team.map(m => m.id));
  };

  const deselectAllMembers = () => {
    setSelectedMembers([]);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setStep(2);
  };

  const handleQuickAlert = async () => {
    if (!selectedCategory || !selectedLocation) {
      toast.error('Please select a location');
      return;
    }

    if (alertMode === 'selected' && selectedMembers.length === 0) {
      toast.error('Please select at least one team member');
      return;
    }
    
    setSending(true);
    try {
      await axios.post(`${API_URL}/alerts`, {
        category: selectedCategory.id,
        location: selectedLocation,
        description: description || `${selectedCategory.label} ${isNonEmergency ? 'issue' : 'alert'} at ${selectedLocation}`,
        target_members: alertMode === 'selected' ? selectedMembers : null,
        severity: isNonEmergency ? 'non_emergency' : 'emergency'
      });
      
      const targetText = alertMode === 'all' 
        ? 'all team members' 
        : `${selectedMembers.length} selected member(s)`;
      toast.success(`Alert sent to ${targetText}!`);
      navigate('/alerts');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send alert');
    } finally {
      setSending(false);
    }
  };

  // Get supported MIME type for MediaRecorder
  const getSupportedMimeType = () => {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/ogg;codecs=opus',
      'audio/wav',
      ''  // Empty string = browser default
    ];
    
    for (const type of types) {
      if (type === '' || MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return '';
  };

  // Voice-to-text using MediaRecorder + Backend Whisper API - IMPROVED
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000, // Optimal for Whisper
          channelCount: 1
        } 
      });
      
      streamRef.current = stream;
      audioChunksRef.current = [];
      
      const mimeType = getSupportedMimeType();
      const options = mimeType ? { 
        mimeType,
        audioBitsPerSecond: 128000 // Higher bitrate for better quality
      } : {};
      
      const recorder = new MediaRecorder(stream, options);
      
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      recorder.onstop = async () => {
        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
        
        if (audioChunksRef.current.length === 0) {
          toast.error('No audio recorded');
          return;
        }
        
        // Create blob from recorded chunks
        const audioBlob = new Blob(audioChunksRef.current, { 
          type: mimeType || 'audio/webm' 
        });
        
        // Check minimum size
        if (audioBlob.size < 1000) {
          toast.error('Recording too short. Hold longer.');
          return;
        }
        
        // Send to backend for transcription
        setIsTranscribing(true);
        toast.info('Processing speech...');
        
        try {
          const formData = new FormData();
          formData.append('file', audioBlob, 'recording.webm');
          
          const response = await axios.post(`${API_URL}/voice/transcribe`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 30000
          });
          
          if (response.data.text && response.data.text.trim()) {
            setDescription(prev => prev + (prev ? ' ' : '') + response.data.text.trim());
            toast.success('Speech recognized!');
          } else {
            toast.info('No speech detected. Try speaking louder.');
          }
        } catch (error) {
          console.error('Transcription error:', error);
          toast.error('Failed to transcribe. Please try again.');
        } finally {
          setIsTranscribing(false);
        }
      };
      
      recorder.start(100); // Collect data every 100ms
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      toast.info('🎤 Recording... Speak now');
      
    } catch (error) {
      console.error('Microphone error:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Microphone access denied. Please allow microphone access in your browser settings.');
      } else if (error.name === 'NotFoundError') {
        toast.error('No microphone found. Please connect a microphone.');
      } else {
        toast.error('Could not access microphone: ' + error.message);
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <div className="min-h-screen bg-void grid-pattern p-4" data-testid="panic-page">
      {/* Header with Logo */}
      <div className="flex items-center gap-4 mb-6">
        <Button 
          variant="outline" 
          size="icon"
          onClick={() => step > 1 ? setStep(step - 1) : navigate('/')}
          className="border-white/20 hover:bg-white/5 rounded-sm"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <PageHeader 
          title={step === 1 ? 'Alert Type' : (isNonEmergency ? 'Issue Details' : 'Location & Details')}
          subtitle={isNonEmergency ? 'Non-Emergency Report' : `Step ${step} of 2`}
        />
      </div>

      {step === 1 && (
        /* Category Selection - 2 cols mobile, 3 cols desktop, aligned boxes */
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3 max-w-4xl mx-auto px-2 auto-rows-fr">
          {ALERT_CATEGORIES.map((category) => {
            const Icon = category.icon;
            return (
              <button
                key={category.id}
                onClick={() => handleCategorySelect(category)}
                className="flex flex-col items-center justify-start py-4 md:py-5 px-2 md:px-4 bg-panel border-2 border-white/10 rounded-sm hover:border-white/30 transition-all text-center h-full"
                data-testid={`category-${category.id}`}
              >
                <div 
                  className="w-10 h-10 md:w-14 md:h-14 rounded-sm flex items-center justify-center mb-2 shrink-0"
                  style={{ backgroundColor: `${category.color}25` }}
                >
                  <Icon className="w-5 h-5 md:w-7 md:h-7" style={{ color: category.color }} />
                </div>
                <h3 
                  className="font-chivo font-black text-lg md:text-2xl uppercase tracking-tight mb-1 shrink-0"
                  style={{ color: category.color }}
                >
                  {category.label}
                </h3>
                <p className="font-mono text-sm md:text-base text-muted-foreground leading-tight flex-1 flex items-start">
                  {category.description}
                </p>
              </button>
            );
          })}
        </div>
      )}

      {step === 2 && selectedCategory && (
        /* Location & Details - Compact layout for quick responses */
        <div className="max-w-4xl mx-auto space-y-3">
          {/* Selected Category - Compact */}
          <div 
            className={`flex items-center gap-3 p-3 bg-panel border rounded-sm ${isNonEmergency ? 'border-orange-500/50' : 'border-white/10'}`}
            style={{ borderColor: isNonEmergency ? undefined : `${selectedCategory.color}50` }}
          >
            <div 
              className="w-10 h-10 rounded-sm flex items-center justify-center"
              style={{ backgroundColor: isNonEmergency ? 'rgba(249, 115, 22, 0.2)' : `${selectedCategory.color}20` }}
            >
              <selectedCategory.icon className="w-5 h-5" style={{ color: isNonEmergency ? '#f97316' : selectedCategory.color }} />
            </div>
            <p className="font-chivo font-bold text-xl md:text-2xl uppercase" style={{ color: isNonEmergency ? '#f97316' : selectedCategory.color }}>
              {selectedCategory.label} {isNonEmergency ? 'Issue' : 'Alert'}
            </p>
          </div>

          {/* Location Grid - All visible, no scrolling */}
          <div className={`bg-panel border rounded-sm p-3 ${isNonEmergency ? 'border-orange-500/20' : 'border-white/10'}`}>
            <p className="font-mono text-sm uppercase text-muted-foreground mb-2 flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Select Location
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
              {LOCATIONS.map((loc) => (
                <button
                  key={loc}
                  onClick={() => setSelectedLocation(loc)}
                  className={`p-2 text-center rounded-sm border transition-all text-base md:text-lg font-mono font-bold ${
                    selectedLocation === loc 
                      ? isNonEmergency 
                        ? 'bg-orange-500/20 border-orange-500 text-orange-500 scale-[1.02]'
                        : 'bg-primary/20 border-primary text-primary scale-[1.02]' 
                      : 'bg-black border-white/10 text-white hover:border-white/30 hover:bg-white/5'
                  }`}
                  data-testid={`loc-${loc.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {loc}
                </button>
              ))}
            </div>
          </div>

          {/* SEND BUTTON - Different styling for non-emergency */}
          <Button
            onClick={handleQuickAlert}
            disabled={!selectedLocation || sending}
            className={`w-full h-16 text-white font-chivo font-black text-2xl uppercase tracking-wide rounded-sm disabled:opacity-50 ${
              isNonEmergency 
                ? 'bg-orange-600 hover:bg-orange-700 shadow-[0_0_30px_rgba(249,115,22,0.5)]'
                : 'bg-alert-red hover:bg-red-700 shadow-[0_0_30px_rgba(239,68,68,0.5)]'
            }`}
            data-testid="send-alert-btn"
          >
            {sending ? (
              'Sending...'
            ) : (
              <>
                <Send className="w-6 h-6 mr-2" />
                {isNonEmergency ? 'Report Issue' : 'Send Alert Now'}
              </>
            )}
          </Button>

          {/* Alert Recipients - Compact */}
          <div className="bg-panel border border-white/10 rounded-sm p-3">
            <p className="font-mono text-sm uppercase text-muted-foreground mb-2 flex items-center gap-2">
              <Users className="w-4 h-4" /> Recipients
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setAlertMode('all')}
                className={`flex-1 p-3 rounded-sm border transition-all text-base md:text-lg font-mono font-bold flex items-center justify-center gap-2 ${
                  alertMode === 'all' 
                    ? 'bg-primary/20 border-primary text-primary' 
                    : 'bg-black border-white/10 text-white hover:border-white/30'
                }`}
              >
                <UsersRound className="w-5 h-5" />
                All Team
              </button>
              <button
                onClick={() => setAlertMode('selected')}
                className={`flex-1 p-3 rounded-sm border transition-all text-base md:text-lg font-mono font-bold flex items-center justify-center gap-2 ${
                  alertMode === 'selected' 
                    ? 'bg-primary/20 border-primary text-primary' 
                    : 'bg-black border-white/10 text-white hover:border-white/30'
                }`}
              >
                <UserCheck className="w-5 h-5" />
                Select Members
              </button>
            </div>

            {/* Individual Member Selection - Only shown if needed */}
            {alertMode === 'selected' && (
              <div className="mt-3 pt-3 border-t border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-[10px] text-muted-foreground">
                    {selectedMembers.length}/{team.length} selected
                  </span>
                  <div className="flex gap-2">
                    <button onClick={selectAllMembers} className="text-[10px] text-primary hover:underline">All</button>
                    <button onClick={deselectAllMembers} className="text-[10px] text-muted-foreground hover:underline">Clear</button>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 max-h-32 overflow-y-auto">
                  {team.map((member) => (
                    <label
                      key={member.id}
                      className={`flex items-center gap-2 p-1.5 rounded-sm cursor-pointer text-xs ${
                        selectedMembers.includes(member.id) 
                          ? 'bg-primary/20 text-primary' 
                          : 'hover:bg-white/5 text-white'
                      }`}
                    >
                      <Checkbox
                        checked={selectedMembers.includes(member.id)}
                        onCheckedChange={() => toggleMember(member.id)}
                        className="w-3 h-3"
                      />
                      <span className="truncate">{member.name.split(' ')[0]}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Description - Optional, collapsed by default feel */}
          <details className="bg-panel border border-white/10 rounded-sm">
            <summary className="p-3 cursor-pointer font-mono text-[10px] uppercase text-muted-foreground hover:text-white">
              + Add Details (Optional)
            </summary>
            <div className="px-3 pb-3 space-y-2">
              <Textarea
                placeholder="Describe the situation..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="bg-black border-white/20 rounded-sm font-inter text-sm min-h-[80px] resize-none"
                data-testid="description-input"
              />
              
              {/* Voice Input */}
              <Button
                type="button"
                variant="outline"
                onClick={toggleRecording}
                disabled={isTranscribing}
                className={`w-full h-10 text-sm font-bold transition-all ${
                  isRecording 
                    ? 'bg-red-600 border-red-500 text-white animate-pulse' 
                    : isTranscribing
                      ? 'bg-blue-600/20 border-blue-500 text-blue-400'
                      : 'border-green-600/50 text-green-500 hover:bg-green-600/10'
                }`}
                data-testid="voice-btn"
              >
                {isTranscribing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Transcribing...
                  </>
                ) : isRecording ? (
                  <>
                    <MicOff className="w-4 h-4 mr-2" />
                    TAP TO STOP
                  </>
                ) : (
                  <>
                    <Mic className="w-4 h-4 mr-2" />
                    Voice to Text
                  </>
                )}
              </Button>
            </div>
          </details>
        </div>
      )}
    </div>
  );
};

export default PanicPage;
